<template>
  <div class="thinking-block my-2" style="max-width: 85%;">
    <!-- Collapsible header -->
    <div
      class="thinking-header d-flex align-center ga-2 py-2 px-1"
      @click="expanded = !expanded"
      style="cursor: pointer;"
    >
      <v-icon :icon="expanded ? 'mdi-chevron-down' : 'mdi-chevron-right'" size="small" color="medium-emphasis" />
      <span :class="['text-body-2', agentStreaming ? 'shimmer-text' : 'text-medium-emphasis']">{{ summaryText }}</span>
    </div>

    <!-- Timeline content -->
    <div v-if="expanded" class="thinking-timeline ml-3">
      <div
        v-for="(item, i) in block.items"
        :key="i"
        class="timeline-item d-flex ga-3"
      >
        <!-- Icon column -->
        <div class="timeline-icon-col d-flex flex-column align-center">
          <v-icon :icon="itemIcon(item)" :color="itemColor(item)" size="small" />
          <div v-if="i < block.items.length - 1 || !isDone" class="timeline-line flex-grow-1" />
        </div>

        <!-- Content column -->
        <div class="timeline-content pb-4 flex-grow-1" style="min-width: 0;">
          <!-- Thinking / Analysis -->
          <template v-if="item._thinking || item._analysis">
            <div class="text-body-2" style="white-space: pre-wrap; word-break: break-word;">
              {{ itemSummary(item) }}
            </div>
          </template>

          <!-- Tool call -->
          <template v-else-if="item.role === 'tool_call'">
            <div class="d-flex align-center ga-2">
              <span class="text-body-2 font-weight-medium">{{ itemLabel(item) }}</span>
              <v-chip v-if="item.status === 'running'" size="x-small" color="info" variant="tonal">{{ t('chat.running') }}</v-chip>
            </div>
            <!-- Expandable tool detail -->
            <v-expansion-panels v-if="item.result || item.error || item.status === 'running'" variant="accordion" class="mt-1 tool-detail-panels">
              <v-expansion-panel>
                <v-expansion-panel-title class="py-1 px-2 text-caption">
                  {{ t('chat.details') }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div v-if="item.syncProgress" class="sync-progress mb-2">
                    <div class="d-flex align-center ga-2 mb-1">
                      <v-icon size="x-small" color="info">mdi-sync</v-icon>
                      <span class="text-caption text-medium-emphasis">{{ item.syncProgress.source }}</span>
                    </div>
                    <div class="text-body-2 mb-1">{{ item.syncProgress.message }}</div>
                    <v-progress-linear
                      v-if="item.syncProgress.total != null"
                      :model-value="Math.round((item.syncProgress.fetched / item.syncProgress.total) * 100)"
                      color="info" height="4" rounded
                    />
                    <v-progress-linear
                      v-else-if="item.syncProgress.phase !== 'done'"
                      indeterminate color="info" height="4" rounded
                    />
                  </div>
                  <div class="text-caption text-medium-emphasis mb-1">{{ t('chat.arguments') }}</div>
                  <pre class="tool-json mb-2">{{ formatJson(item.arguments) }}</pre>
                  <template v-if="item.result">
                    <div class="text-caption text-medium-emphasis mb-1">{{ t('chat.result') }}</div>
                    <pre class="tool-json">{{ truncate(item.result, 2000) }}</pre>
                  </template>
                  <template v-if="item.error">
                    <div class="text-caption text-error mb-1">{{ t('chat.error') }}</div>
                    <pre class="tool-json text-error">{{ item.error }}</pre>
                  </template>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
        </div>
      </div>

      <!-- Done marker -->
      <div v-if="isDone" class="timeline-item d-flex ga-3">
        <div class="timeline-icon-col d-flex flex-column align-center">
          <v-icon icon="mdi-check-circle-outline" color="success" size="small" />
        </div>
        <div class="timeline-content">
          <span class="text-body-2 font-weight-medium">{{ t('chat.done') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { ref, computed } from 'vue'

const props = defineProps({
  block: { type: Object, required: true },
  agentStreaming: { type: Boolean, default: false },
  // Live status for the active block while the turn is paused on a frontend
  // tool (host's setStatus text / pending call summary / waiting label). Wins
  // over the tool summary in the header so the wait on the user is explicit.
  status: { type: String, default: '' },
})

const expanded = ref(false)

const isStreaming = computed(() =>
  props.block.items.some(item => item._streaming || item.status === 'running')
)

const isDone = computed(() => !isStreaming.value)

const summaryText = computed(() => {
  if (props.agentStreaming && props.status) {
    const s = props.status.trim()
    return s.length > 80 ? s.slice(0, 80) + '...' : s
  }

  const toolCalls = props.block.items.filter(i => i.role === 'tool_call')
  const thinkingItems = props.block.items.filter(i => i._thinking || i._analysis)

  // Prefer the most recent tool call's model-written summary as the one-line
  // status (e.g. "Searching issues for water damage"). It reads better than a
  // bare "Thinking..." or tool name, is exactly what the field exists for, and
  // doubles as a live "what's it doing" label while the call is still running.
  const summarised = [...toolCalls].reverse().find(t => t.summary)
  if (summarised) {
    const s = String(summarised.summary).trim()
    return s.length > 80 ? s.slice(0, 80) + '...' : s
  }

  if (isStreaming.value) return t('chat.thinking')

  if (thinkingItems.length > 0) {
    const text = (thinkingItems[0].content || '').trim().split('\n')[0]
    const summary = text.length > 80 ? text.slice(0, 80) + '...' : text
    if (toolCalls.length > 0) {
      const used = toolCalls.length === 1 ? t('chat.toolUsedOne') : t('chat.toolsUsedCount', { count: toolCalls.length })
      return `${summary} (${used})`
    }
    return summary
  }

  if (toolCalls.length > 0) {
    const names = [...new Set(toolCalls.map(t => t.name))]
    if (names.length <= 3) return t('chat.usedNames', { names: names.join(', ') })
    return t('chat.usedToolsCount', { count: toolCalls.length })
  }

  return t('chat.thinkingComplete')
})

function itemIcon(item) {
  if (item._thinking || item._analysis) return 'mdi-timer-outline'
  if (item.role === 'tool_call') {
    if (item.status === 'running') return 'mdi-loading mdi-spin'
    if (item.status === 'error') return 'mdi-alert-circle-outline'
    return 'mdi-wrench-outline'
  }
  return 'mdi-circle-small'
}

function itemColor(item) {
  if (item._thinking || item._analysis) return 'medium-emphasis'
  if (item.role === 'tool_call') {
    if (item.status === 'running') return 'info'
    if (item.status === 'error') return 'error'
    return 'success'
  }
  return undefined
}

function itemSummary(item) {
  const text = (item.content || '').trim()
  if (text.length > 300) return text.slice(0, 300) + '...'
  return text
}

// Tool-call row label: the model's one-line intent summary when present, else
// the raw tool name.
function itemLabel(item) {
  return item.summary || item.name
}

function formatJson(obj) {
  if (!obj) return ''
  if (typeof obj === 'string') return obj
  try { return JSON.stringify(obj, null, 2) } catch { return String(obj) }
}

function truncate(str, max) {
  if (!str || str.length <= max) return str
  return str.slice(0, max) + '\n... (truncated)'
}
</script>

<style scoped>
.thinking-header:hover {
  opacity: 0.8;
}
.timeline-line {
  width: 1.5px;
  min-height: 12px;
  background: rgba(var(--v-theme-on-surface), 0.15);
  margin: 4px 0;
}
.timeline-icon-col {
  width: 20px;
  flex-shrink: 0;
}
.tool-detail-panels :deep(.v-expansion-panel) {
  background: transparent;
}
.tool-detail-panels :deep(.v-expansion-panel-title) {
  min-height: 28px;
  padding: 2px 8px;
}
.tool-json {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 6px;
  padding: 8px;
  font-size: 0.75rem;
  font-family: 'Fira Code', 'Consolas', monospace;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 200px;
  overflow-y: auto;
}
.v-theme--dark .tool-json {
  background: rgba(255, 255, 255, 0.03);
}
.sync-progress {
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-info), 0.05);
  border-left: 3px solid rgb(var(--v-theme-info));
}
/* Text shimmer animation — light sweep across the text */
.shimmer-text {
  background: linear-gradient(
    90deg,
    rgba(var(--v-theme-on-surface), 0.4) 0%,
    rgba(var(--v-theme-on-surface), 0.4) 40%,
    rgba(var(--v-theme-on-surface), 0.9) 50%,
    rgba(var(--v-theme-on-surface), 0.4) 60%,
    rgba(var(--v-theme-on-surface), 0.4) 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: text-shimmer 1.4s ease-in-out infinite;
}
@keyframes text-shimmer {
  0%   { background-position: 100% 50%; }
  100% { background-position: -100% 50%; }
}
</style>
